import type { Auth, User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { go, useSection } from "./hooks";
import { dateLabel } from "./format";
import { UNDO_MS } from "./deletion";
import {
  bandText,
  callsPair,
  canReopen,
  closedText,
  closeProblem,
  countText,
  cycleText,
  dayText,
  gallonsText,
  KIND_TEXT,
  type LostAction,
  lostFailure,
  lostSavedText,
  LOST_STATE_TEXT,
  measureIcon,
  measures,
  measureText,
  NOTE_MAX,
  personText,
  problemId,
  REASON_TEXT,
  rebateCountText,
  rebateDiffText,
  STATE_TEXT,
  watched,
  watchParam,
  watchSearch,
  weeksText,
} from "./cutoverWatch";
import { type Failure, failureOf, Problem } from "./UserPages";
import { Skeleton } from "./Skeleton";

/** Design 26.11 F5: the watch of each area on the app, for its first full cycle. */
export function CutoverWatch({ base, auth, user }: { base: string; auth: Auth; user: User }) {
  const [found, setFound] = useState<api.CutoverWatch | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [picked, setPickedState] = useState<string | null>(() => watchParam(location.search));
  const table = useRef<HTMLTableElement>(null);
  const setPicked = (id: string | null) => {
    setPickedState(id);
    history.replaceState(history.state, "", `${location.pathname}${watchSearch(location.search, id)}${location.hash}`);
  };
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.cutoverWatch)
      .then((page) => live && setFound(page))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  useSection(location.hash === "#watch" ? location.hash : "", found === undefined ? "loading" : "shown");
  // An API before roadmap 088 has no watch: the section does not show.
  if (found === "hidden") return null;
  const page = typeof found === "object" ? found : null;
  const areas = page ? watched(page.areas) : [];
  const open = areas.filter((area) => area.state === "open").length;
  const area = areas.find((one) => one.area_id === picked);
  const closeDetail = (code: string) => {
    setPicked(null);
    requestAnimationFrame(() => table.current?.querySelector<HTMLButtonElement>(`tr[data-area="${code}"] [data-testid=cutover-watch-open]`)?.focus());
  };
  return (
    <details className="parity-ready" open id="watch" tabIndex={-1} data-testid="cutover-watch">
      <summary>
        <h2>Watch after the switch</h2>{" "}
        {page && (
          <span className="sub" data-testid="cutover-watch-count">
            {countText(areas.length, open)}
          </span>
        )}
      </summary>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : !areas.length ? (
        <p data-testid="cutover-watch-empty">
          No area is on the app yet. After its switch date, an area shows its watch here each day. Switch an area on the{" "}
          <a href={`${base}/cutover`} onClick={go} data-testid="cutover-watch-cutover">
            Cutover page
          </a>
          .
        </p>
      ) : (
        <>
          <p className="sub">
            The watch covers the first full cycle on the app. It passes when the cycle ends with no open lost pickup, no rebate difference, the gallons in the band, and no more overflows and calls at fault than in the shadow.
          </p>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Watch by area">
            <table ref={table} className="list watch-table" data-testid="cutover-watch-rows">
              <thead>
                <tr>
                  <th scope="col">Area</th>
                  <th scope="col">Watch</th>
                  <th scope="col">Cycle</th>
                  <th scope="col" className="num">
                    Lost open
                  </th>
                  <th scope="col" className="num">
                    Gallons
                  </th>
                  <th scope="col">Overflows / calls at fault</th>
                  <th scope="col" className="num">
                    Rebate differences
                  </th>
                  <th scope="col" className="num">
                    Visits
                  </th>
                </tr>
              </thead>
              <tbody>
                {areas.map((row) => (
                  <tr key={row.area_id} data-testid="cutover-watch-row" data-area={row.code} data-state={row.state}>
                    <th scope="row">
                      <button
                        type="button"
                        className="link"
                        data-testid="cutover-watch-open"
                        aria-pressed={picked === row.area_id}
                        onClick={() => setPicked(row.area_id)}
                      >
                        {row.code}
                      </button>
                    </th>
                    <td data-label="Watch">
                      <span className={row.state === "failed" ? "chip-flag" : "chip-flag neutral"}>{STATE_TEXT[row.state]}</span>
                    </td>
                    <td data-label="Cycle">
                      {dayText(row)}
                      <span className="sub line">{cycleText(row)}</span>
                    </td>
                    <td data-label="Lost open" className={row.lost_open ? "num error" : "num"}>
                      {row.lost_open.toLocaleString("en-US")}
                      {row.lost && <span className="sub lost-of"> of {row.lost.total.toLocaleString("en-US")}</span>}
                    </td>
                    <td data-label="Gallons" className="num wrap">
                      {gallonsText(row.gallons)}
                      <span className="sub line">Band: {bandText(row.gallons)}</span>
                    </td>
                    <td data-label="Overflows / calls at fault" className={(row.overflows && !row.overflows.ok) || (row.at_fault && !row.at_fault.ok) ? "error" : undefined}>
                      {callsPair(row.overflows, row.at_fault)[0]}
                      <span className="sub line">{callsPair(row.overflows, row.at_fault)[1]}</span>
                    </td>
                    <td data-label="Rebate differences" className={row.rebates?.differences ? "num error" : "num"}>
                      {rebateCountText(row.rebates)}
                    </td>
                    <td data-label="Visits" className="num">
                      {row.visits ?? "–"}
                      {row.corrections ? <span className="sub line">{row.corrections} corrected</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {area && (
            <WatchDetail
              key={area.area_id}
              base={base}
              auth={auth}
              user={user}
              area={area}
              onClose={() => closeDetail(area.code)}
              onSaved={() => setAttempt((n) => n + 1)}
            />
          )}
        </>
      )}
    </details>
  );
}

function WatchDetail({
  base,
  auth,
  user,
  area,
  onClose,
  onSaved,
}: {
  base: string;
  auth: Auth;
  user: User;
  area: api.WatchArea;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [found, setFound] = useState<api.WatchDetail | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.cutoverWatchArea(token, area.area_id))
      .then((page) => live && setFound(page))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, area.area_id, attempt]);
  const page = typeof found === "object" ? found : null;
  const saved = () => {
    setAttempt((n) => n + 1);
    onSaved();
  };
  return (
    <section className="parity-ready-detail" aria-labelledby="cutover-watch-title" data-testid="cutover-watch-detail">
      <h3 id="cutover-watch-title">
        {area.code} · {STATE_TEXT[area.state]}
      </h3>
      <ul className="watch-measures" data-testid="cutover-watch-measures">
        {measures(area).map((measure) => (
          <li key={measure.key} data-measure={measure.key} data-ok={measure.ok === null ? "pending" : measure.ok ? "ok" : "fail"}>
            <span className={`chip-flag ${measure.ok === null ? "neutral" : measure.ok ? "ok" : "error"}`}>
              <span aria-hidden="true">{measureIcon(measure.ok)}</span> {measureText(measure.ok)}
            </span>{" "}
            {measure.label}
          </li>
        ))}
      </ul>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : typeof found === "string" ? (
        found === "hidden" ? null : <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        page && (
          <>
            <h4>Gallons by week</h4>
            <div className="table-scroll" tabIndex={0} role="region" aria-label="Gallons by week">
              <table className="list" data-testid="cutover-watch-weeks">
                <thead>
                  <tr>
                    <th scope="col">Week</th>
                    <th scope="col" className="num">
                      App
                    </th>
                    <th scope="col" className="num">
                      Shadow cycles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {page.weeks.map((week) => (
                    <tr key={week.week_start}>
                      <th scope="row">{dateLabel(week.week_start)}</th>
                      <td data-label="App" className="num">
                        {week.app === null ? "–" : Math.round(week.app).toLocaleString("en-US")}
                      </td>
                      <td data-label="Shadow cycles" className="num">
                        {week.shadow.map((value) => Math.round(value).toLocaleString("en-US")).join(" / ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="sub">
              The band of the cycle: {bandText(page.gallons)}, from the same weeks of the last 2 cycles and the estimate ({Math.round(page.gallons?.estimate ?? 0).toLocaleString("en-US")} gal).
            </p>
            <h4>Lost pickups</h4>
            {!page.lost_rows.length ? (
              <p data-testid="cutover-watch-no-lost">No lost pickup.</p>
            ) : (
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Lost pickups">
                <table className="list watch-lost" data-testid="cutover-watch-lost">
                  <thead>
                    <tr>
                      <th scope="col">Account</th>
                      <th scope="col">Why</th>
                      <th scope="col">Week</th>
                      <th scope="col">Owner</th>
                      <th scope="col">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.lost_rows.map((lost) => (
                      <LostRow key={`${lost.account_id}-${lost.kind}-${lost.due_on}`} base={base} user={user} page={page} lost={lost} onSaved={saved} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {page.lost_rows.length >= page.limit && <p className="footnote">The first {page.limit.toLocaleString("en-US")} rows only.</p>}
            {page.rebate_rows.length > 0 && (
              <>
                <h4>Rebate differences</h4>
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Rebate differences">
                  <table className="list" data-testid="cutover-watch-rebates">
                    <thead>
                      <tr>
                        <th scope="col">Account</th>
                        <th scope="col" className="num">
                          App gallons
                        </th>
                        <th scope="col" className="num">
                          Source gallons
                        </th>
                        <th scope="col">Difference</th>
                        <th scope="col">Weeks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {page.rebate_rows.map((row) => (
                        <tr key={row.account_id}>
                          <th scope="row">
                            <a href={`${base}/accounts/${row.account_id}`} onClick={go}>
                              {row.number}
                            </a>{" "}
                            <span className="sub">{row.name}</span>
                          </th>
                          <td data-label="App gallons" className="num">
                            {row.app_gal.toLocaleString("en-US")}
                          </td>
                          <td data-label="Source gallons" className="num">
                            {row.source_gal.toLocaleString("en-US")}
                          </td>
                          <td data-label="Difference" className="error">
                            {rebateDiffText(row.app_gal, row.source_gal)}
                          </td>
                          <td data-label="Weeks" className="wrap">
                            {weeksText(row.weeks)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <h4>Visits and corrections each day</h4>
            {!page.people.length ? (
              <p data-testid="cutover-watch-no-visits">No visit yet.</p>
            ) : (
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Visits and corrections">
                <table className="list" data-testid="cutover-watch-people">
                  <thead>
                    <tr>
                      <th scope="col">Day</th>
                      <th scope="col">Person</th>
                      <th scope="col" className="num">
                        Visits
                      </th>
                      <th scope="col" className="num">
                        Corrections
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.people.map((row) => (
                      <tr key={`${row.day}-${row.member_id ?? ""}`}>
                        <th scope="row">{dateLabel(row.day)}</th>
                        <td data-label="Person">{personText(row.name) || "Unknown"}</td>
                        <td data-label="Visits" className="num">
                          {row.visits}
                        </td>
                        <td data-label="Corrections" className="num">
                          {row.corrections}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )
      )}
      <p className="actions">
        <button type="button" className="quiet" data-testid="cutover-watch-close-detail" onClick={onClose}>
          Close
        </button>
      </p>
    </section>
  );
}

function LostRow({ base, user, page, lost, onSaved }: { base: string; user: User; page: api.WatchDetail; lost: api.WatchLost; onSaved: () => void }) {
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState<api.WatchReason | "">("");
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<LostAction | null>(null);
  const [undo, setUndo] = useState(false);
  const reasonRef = useRef<HTMLSelectElement>(null);
  const openRef = useRef<HTMLButtonElement>(null);
  const stateRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (closing) requestAnimationFrame(() => reasonRef.current?.focus());
  }, [closing]);
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(false), UNDO_MS);
    return () => window.clearTimeout(timer);
  }, [undo]);
  const key = { area_id: page.area_id, account_id: lost.account_id, kind: lost.kind, due_on: lost.due_on };
  const send = async (body: api.WatchLostAction) => {
    setBusy(true);
    setError(null);
    setSaved(null);
    setUndo(false);
    try {
      await api.watchLostAction(await user.getIdToken(), body);
      setBusy(false);
      setClosing(false);
      setReason("");
      setNote("");
      setTouched(false);
      setSaved(body.action);
      setUndo(body.action === "close");
      onSaved();
      if (body.action !== "owner") requestAnimationFrame(() => stateRef.current?.focus());
    } catch (e) {
      setError(lostFailure(e, body.action));
      setBusy(false);
    }
  };
  const cancel = () => {
    setClosing(false);
    setTouched(false);
    requestAnimationFrame(() => openRef.current?.focus());
  };
  const problem = closeProblem(reason, note);
  const problemFor = problemId(lost);
  const reopenable = canReopen(page.can_edit, lost);
  return (
    <tr data-testid="cutover-watch-lost-row" data-state={lost.state}>
      <th scope="row">
        <a href={`${base}/accounts/${lost.account_id}`} onClick={go}>
          {lost.number}
        </a>
        <span className="sub line">{lost.name}</span>
      </th>
      <td data-label="Why" className="wrap">
        {KIND_TEXT[lost.kind]}
      </td>
      <td data-label="Week">{dateLabel(lost.due_on)}</td>
      <td data-label="Owner" className="owner">
        {page.can_edit && lost.state === "open" ? (
          <select
            data-testid="cutover-watch-owner"
            aria-label={`Owner of the lost pickup of ${lost.number}`}
            value={lost.owner_id ?? ""}
            disabled={busy}
            onChange={(e) => e.target.value && void send({ ...key, action: "owner", owner_id: e.target.value })}
          >
            {!lost.owner_id && <option value="">No owner</option>}
            {page.owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {personText(owner.name)}
              </option>
            ))}
            {lost.owner_id && !page.owners.some((owner) => owner.id === lost.owner_id) && <option value={lost.owner_id}>{personText(lost.owner) || "A former member"}</option>}
          </select>
        ) : (
          personText(lost.owner) || "–"
        )}
        {saved === "owner" && (
          <span className="sub line" role="status" data-testid="cutover-watch-owner-saved">
            {lostSavedText("owner")}
          </span>
        )}
      </td>
      <td data-label="State" className="wrap">
        <span ref={stateRef} tabIndex={-1} data-testid="cutover-watch-lost-state">
          {LOST_STATE_TEXT[lost.state]}
        </span>
        {lost.state !== "open" && <span className="sub line">{closedText(lost)}</span>}
        {saved && saved !== "owner" && (
          <span className="sub line" role="status" data-testid="cutover-watch-lost-saved">
            {lostSavedText(saved)}
          </span>
        )}
        {saved === "close" && undo && reopenable && (
          <button type="button" className="quiet" data-testid="cutover-watch-undo" disabled={busy} onClick={() => void send({ ...key, action: "reopen" })}>
            Undo
          </button>
        )}
        {page.can_edit && lost.state === "open" && !closing && (
          <>
            {" "}
            <button ref={openRef} type="button" className="quiet" data-testid="cutover-watch-close" onClick={() => setClosing(true)}>
              Close with a reason
            </button>
          </>
        )}
        {reopenable && !undo && (
          <>
            {" "}
            <button type="button" className="quiet" data-testid="cutover-watch-reopen" disabled={busy} onClick={() => void send({ ...key, action: "reopen" })}>
              {busy ? "Saving…" : "Open again"}
            </button>
          </>
        )}
        {closing && (
          <form
            data-testid="cutover-watch-close-form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setTouched(true);
              if (!problem && reason) void send({ ...key, action: "close", reason, note });
            }}
          >
            <select
              ref={reasonRef}
              data-testid="cutover-watch-reason"
              aria-label="Reason"
              aria-describedby={problem ? problemFor : undefined}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value as api.WatchReason | "");
                setTouched(true);
              }}
            >
              <option value="">Pick a reason</option>
              {page.reasons.map((value) => (
                <option key={value} value={value}>
                  {REASON_TEXT[value] ?? value}
                </option>
              ))}
            </select>{" "}
            <input
              data-testid="cutover-watch-note"
              aria-label="Note"
              aria-describedby={problem ? problemFor : undefined}
              aria-invalid={touched && Boolean(problem) && Boolean(reason)}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note"
            />{" "}
            <button type="submit" className="primary" disabled={busy || Boolean(problem)} aria-describedby={problem ? problemFor : undefined}>
              {busy ? "Saving…" : "Close it"}
            </button>{" "}
            <button type="button" className="quiet" onClick={cancel} disabled={busy}>
              Cancel
            </button>
            {problem && (
              <span id={problemFor} className={touched ? "sub line error" : "sub line"} data-testid="cutover-watch-close-problem">
                {problem}
              </span>
            )}
            <span className="sub line" data-testid="cutover-watch-note-count">
              {note.length.toLocaleString("en-US")} of {NOTE_MAX} characters
            </span>
          </form>
        )}
        {error && (
          <p role="alert" className="error" data-testid="cutover-watch-lost-error">
            {error}
          </p>
        )}
      </td>
    </tr>
  );
}
